import React from 'react';
import { askWriteback, type WritebackAnswers } from './worksheetPrompts';

/**
 * D98.12: the writeback pick as an in-page form instead of four window.prompt calls.
 * The fields are fed through askWriteback, so the validation and the submitted object are the
 * prompts' own (one owner). Props: `costOnly` = a sent invoice (D96.2/D96.15: only the TT$ cost);
 * `onSubmit(answers)` gets the same `WritebackAnswers` the prompts produce; page adds expectedUpdatedAt.
 */
type Props = {
  lineNumber: number;
  costOnly: boolean;
  onSubmit: (answers: WritebackAnswers) => void;
  onCancel: () => void;
  busy?: boolean;
};

const field = 'h-8 w-full rounded border border-slate-300 bg-white px-2 text-sm';

export function WritebackForm({ lineNumber, costOnly, onSubmit, onCancel, busy = false }: Props) {
  const [list, setList] = React.useState('');
  const [line, setLine] = React.useState(String(lineNumber));
  const [qty, setQty] = React.useState('');
  const [cost, setCost] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // askWriteback asks list, line, (qty), cost in that order; the form answers from its fields.
    const answers = [list, line, ...(costOnly ? [] : [qty]), cost];
    const result = askWriteback(() => answers.shift() ?? null, { costOnly });
    if (result.kind === 'refused') return setError(result.message);
    if (result.kind === 'ok') {
      setError(null);
      onSubmit(result.value);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2 rounded border border-slate-200 bg-slate-50 p-3 text-sm" aria-label="Record actual pick">
      {costOnly && <p className="text-xs text-slate-600">This invoice is sent: only the TT$ cost can be written back.</p>}
      <label className="block text-xs text-slate-600">
        Write back to
        <select className={field} value={list} onChange={(e) => setList(e.target.value)}>
          <option value="">Choose a list</option>
          <option value="stock">Stock</option>
          <option value="pantry">Pantry</option>
        </select>
      </label>
      <label className="block text-xs text-slate-600">
        Line number
        <input className={field} inputMode="numeric" value={line} onChange={(e) => setLine(e.target.value)} />
      </label>
      {!costOnly && (
        <label className="block text-xs text-slate-600">
          Actual qty picked
          <input className={field} inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
        </label>
      )}
      <label className="block text-xs text-slate-600">
        TT$ cost per unit{costOnly ? '' : ' (optional)'}
        <input className={field} inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} />
      </label>
      {error && (
        <p role="alert" className="text-xs font-medium text-rose-700">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700">
          Cancel
        </button>
        <button type="submit" disabled={busy} className="rounded bg-primary-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
          Record pick
        </button>
      </div>
    </form>
  );
}
